import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

import { Diagnostics } from "../../../../services";

import { DEFAULT_SERVICE, RangeKey, REFRESH_INTERVAL } from "./constants";

interface ContextValue {
	services: Diagnostics.ServicesResponse["items"];
	service: string;
	setService: (value: string) => void;
	range: RangeKey;
	setRange: (value: RangeKey) => void;
	rangeMinutes: number;
	/**
	 * Changes on every refresh: tabs depend on it and re-fetch their own data
	 * without knowing about each other.
	 */
	refreshToken: number;
	refresh: () => void;
}

const SystemPerformanceContext = createContext<ContextValue | null>(null);

const RANGE_MINUTES: Record<RangeKey, number> = {
	hour: 60,
	threeHours: 180,
	twelveHours: 720,
	day: 1440,
	threeDays: 3 * 24 * 60,
};

export const SystemPerformanceProvider: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	const [services, setServices] = useState<
		Diagnostics.ServicesResponse["items"]
	>([]);
	const [service, setService] = useState(DEFAULT_SERVICE);
	const [range, setRange] = useState<RangeKey>("threeHours");
	const [refreshToken, setRefreshToken] = useState(0);

	const refresh = useCallback(
		() => setRefreshToken((value) => value + 1),
		[],
	);

	useEffect(() => {
		let cancelled = false;

		Diagnostics.getServices()
			.then((response) => {
				if (cancelled || !response?.items?.length) return;

				setServices(response.items);

				// When the default service is absent from the cluster, fall
				// back to the first one that actually reports diagnostics.
				const known = response.items.some(
					(item) => item.service === service,
				);
				if (!known) setService(response.items[0].service);
			})
			.catch(() => null);

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshToken]);

	// Re-arm the timer on every refresh (auto or manual) so the countdown
	// always shows a full interval until the next update.
	useEffect(() => {
		const timer = setInterval(refresh, REFRESH_INTERVAL);
		return () => clearInterval(timer);
	}, [refresh, refreshToken]);

	const value = useMemo<ContextValue>(
		() => ({
			services,
			service,
			setService,
			range,
			setRange,
			rangeMinutes: RANGE_MINUTES[range],
			refreshToken,
			refresh,
		}),
		[services, service, range, refreshToken, refresh],
	);

	return (
		<SystemPerformanceContext.Provider value={value}>
			{children}
		</SystemPerformanceContext.Provider>
	);
};

export function useSystemPerformance(): ContextValue {
	const context = useContext(SystemPerformanceContext);

	if (!context) {
		throw new Error(
			"useSystemPerformance must be used inside SystemPerformanceProvider",
		);
	}

	return context;
}

export default SystemPerformanceContext;
